import './index.css'
import cn from 'clsx'
import * as React from 'react'
import { connect } from 'react-redux'
import type { PlaylistTheme } from 'types/playlist'
import type { State } from 'types/redux'
import {
  PLAYLIST_THEMES_LIMIT_AMOUNT,
  PLAYLIST_THEMES_UNLIMITED,
} from '@disco/constants/FeatureTypes'
import {
  createTheme,
  deleteTheme,
  loadThemes,
  updateTheme,
} from '@disco/state-management/reducers/themes'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import DiscoModal from 'components/ui/disco-modal'
import { RadioButton } from 'components/ui/radio'
import EditTheme from '../edit-theme'
import Theme from '../theme'

function mapStateToProps(state: State): any {
  const isFeatureEnabled = makeIsFeatureEnabled()
  const featureEnabled = isFeatureEnabled(state, {
    featureName: PLAYLIST_THEMES_UNLIMITED,
  })
  return { ...state.themes, themesLimit: !featureEnabled }
}

const mapDispatchToProps = {
  loadThemes,
  deleteTheme,
  updateTheme,
  createTheme,
}

class ThemeExplorer extends React.Component<
  {
    loadThemes: typeof loadThemes
    deleteTheme: typeof deleteTheme
    updateTheme: typeof updateTheme
    createTheme: typeof createTheme
    isFetched: boolean
    isFetching: boolean
    isCreating?: boolean
    activeTheme?: null | PlaylistTheme
    themes: Array<PlaylistTheme>
    fullWidth?: boolean
    onCancelCreating?: () => unknown
    onSave?: (theme: null | PlaylistTheme) => unknown
    onSelect?: (theme: null | PlaylistTheme) => unknown
    themesLimit: boolean
  },
  {
    modalOpen: boolean
  }
> {
  state = {
    modalOpen: false,
  }

  componentDidMount() {
    if (!this.props.isFetched && !this.props.isFetching) this.props.loadThemes()
  }

  _onSave = (theme: PlaylistTheme) => {
    if (this.props.onSave) {
      this.props.onSave(theme)
    }
  }
  _isDisabledTheme = (index: number): boolean => {
    return this.props.themesLimit && index >= PLAYLIST_THEMES_LIMIT_AMOUNT
  }

  _renderThemes() {
    const { onSelect } = this.props
    let formattedThemes = null

    if (this.props.themesLimit) {
      formattedThemes = [...this.props.themes]
      formattedThemes.sort((theme1, theme2) => {
        return theme1.modified > theme2.modified ? -1 : 1
      })
      const defaultThemeIndex = formattedThemes.findIndex(
        theme => theme.default,
      )
      const defaultTheme = formattedThemes[defaultThemeIndex]

      if (defaultTheme) {
        formattedThemes.splice(defaultThemeIndex, 1)
        formattedThemes.unshift(defaultTheme)
      }
    }

    const themes = formattedThemes ? formattedThemes : this.props.themes
    return (
      <>
        {onSelect && (
          <div className="ThemeExplorer_Theme">
            <div className="Theme" onClick={() => onSelect(null)}>
              <div className="Theme_Content_Bg">
                <div className="Theme_Content_Logo" />
              </div>
              <div className="Theme_Details">
                <div className="Theme_Details_Name">No theme</div>
              </div>
              <RadioButton checked={!this.props.activeTheme} />
            </div>
          </div>
        )}
        {themes.map((theme, index) => {
          const isDisabledTheme = this._isDisabledTheme(index)

          return (
            <div
              key={index}
              className={cn('ThemeExplorer_Theme', {
                'ThemeExplorer_Theme--Disabled': isDisabledTheme,
              })}
            >
              <Theme
                deleteTheme={this.props.deleteTheme}
                updateTheme={this.props.updateTheme}
                createTheme={this.props.createTheme}
                theme={theme}
                labelMark
                isSelected={Boolean(
                  this.props.activeTheme &&
                    this.props.activeTheme.id === theme.id,
                )}
                onSelect={onSelect ? () => onSelect(theme) : undefined}
                onUnselect={onSelect ? () => onSelect(null) : undefined}
                disabled={isDisabledTheme}
              />
            </div>
          )
        })}
      </>
    )
  }

  _renderCreateTheme() {
    return (
      <EditTheme
        theme={null}
        onSave={this._onSave}
        closeModal={this.props.onCancelCreating}
      />
    )
  }

  render() {
    const { fullWidth } = this.props
    return (
      <div
        className={cn('ThemeExplorer', {
          fullWidth,
        })}
      >
        {this.props.isCreating && (
          <DiscoModal onRequestClose={this.props.onCancelCreating}>
            {this._renderCreateTheme()}
          </DiscoModal>
        )}
        {this._renderThemes()}
      </div>
    )
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ThemeExplorer) as any
